import { useState, useCallback, useEffect } from "react";
import {
  ChecklistItemEntity,
  PaginationInfoEntity,
  GetChecklistItemsUseCase,
  SearchChecklistItemsUseCase,
  ChecklistItemsTabStatus,
} from "../../domain";
import { ChecklistRepositoryImpl } from "../../data/repositories/ChecklistRepositoryImpl";

// Create repository instance (singleton)
const checklistRepository = new ChecklistRepositoryImpl();

// Create use cases
const getChecklistItemsUseCase = new GetChecklistItemsUseCase(
  checklistRepository
);
const searchChecklistItemsUseCase = new SearchChecklistItemsUseCase(
  checklistRepository
);

export interface UseChecklistItemsReturn {
  items: ChecklistItemEntity[];
  pagination: PaginationInfoEntity | null;
  loading: boolean;
  error: string | null;
  fetchItems: (offset?: number) => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  updateItemStatus: (
    itemId: number,
    status: ChecklistItemEntity["status"],
    updates?: Partial<ChecklistItemEntity>
  ) => void;
  clearError: () => void;
}

const itemMatchesTabStatus = (
  itemStatus: ChecklistItemEntity["status"],
  tabStatus?: ChecklistItemsTabStatus | null
): boolean => {
  if (!tabStatus) return true;

  if (tabStatus === "pending") {
    return (
      itemStatus === "not_started" ||
      itemStatus === "in_progress" ||
      itemStatus === "submitted_for_review"
    );
  }

  if (tabStatus === "done") {
    return itemStatus === "done" || itemStatus === "approved";
  }

  return itemStatus === tabStatus;
};

export const useChecklistItems = (
  month: string,
  accountingArea: string | null,
  status?: ChecklistItemsTabStatus | null,
  /** Trimmed, debounced query; empty string uses list endpoint */
  searchQuery: string = ""
): UseChecklistItemsReturn => {
  const [items, setItems] = useState<ChecklistItemEntity[]>([]);
  const [pagination, setPagination] = useState<PaginationInfoEntity | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentOffset, setCurrentOffset] = useState(0);
  const limit = 20;

  /**
   * Fetch checklist items for selected accounting area
   */
  const fetchItems = useCallback(
    async (offset: number = 0): Promise<void> => {
      if (!accountingArea) {
        setItems([]);
        setPagination(null);
        return;
      }

      setLoading(true);
      setError(null);

      // Clear items if starting from offset 0 (new area or refresh)
      if (offset === 0) {
        setItems([]);
        setPagination(null);
        setCurrentOffset(0);
      }

      try {
        const result =
          searchQuery.length > 0
            ? await searchChecklistItemsUseCase.execute(
                month,
                accountingArea,
                searchQuery,
                offset,
                limit,
                status
              )
            : await getChecklistItemsUseCase.execute(
                month,
                accountingArea,
                offset,
                limit,
                status
              );

        if (offset === 0) {
          // Replace items for new fetch
          setItems(result.items);
        } else {
          // Append items for load more
          setItems((prevItems) => [...prevItems, ...result.items]);
        }

        setPagination(result.pagination);
        setCurrentOffset(offset + result.items.length);
      } catch (err: any) {
        const errorMessage = err.message || "Failed to load checklist items";
        setError(errorMessage);
        console.error("Error fetching checklist items:", err);
      } finally {
        setLoading(false);
      }
    },
    [month, accountingArea, limit, status, searchQuery]
  );

  /**
   * Load more items (infinite scroll)
   */
  const loadMore = useCallback(async (): Promise<void> => {
    if (pagination?.hasMore && !loading) {
      await fetchItems(currentOffset);
    }
  }, [pagination, loading, currentOffset, fetchItems]);

  /**
   * Refresh items (reset and fetch from beginning)
   */
  const refresh = useCallback(async (): Promise<void> => {
    await fetchItems(0);
  }, [fetchItems]);

  /**
   * Apply a successful item mutation to the currently loaded page.
   */
  const updateItemStatus = useCallback(
    (
      itemId: number,
      nextStatus: ChecklistItemEntity["status"],
      updates: Partial<ChecklistItemEntity> = {}
    ): void => {
      const shouldKeepInCurrentTab = itemMatchesTabStatus(nextStatus, status);

      setItems((prevItems) =>
        prevItems.flatMap((item) => {
          if (item.id !== itemId) return [item];

          const updatedItem: ChecklistItemEntity = {
            ...item,
            ...updates,
            status: nextStatus,
            updated_at: updates.updated_at || new Date().toISOString(),
          };

          return shouldKeepInCurrentTab ? [updatedItem] : [];
        })
      );

      if (!shouldKeepInCurrentTab) {
        setPagination((prevPagination) =>
          prevPagination
            ? {
                ...prevPagination,
                total: Math.max(prevPagination.total - 1, 0),
              }
            : prevPagination
        );
      }
    },
    [status]
  );

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reset items when month, accounting area, or status changes
  useEffect(() => {
    setItems([]);
    setPagination(null);
    setCurrentOffset(0);
  }, [month, accountingArea, status, searchQuery]);

  return {
    items,
    pagination,
    loading,
    error,
    fetchItems,
    loadMore,
    refresh,
    updateItemStatus,
    clearError,
  };
};

